import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useProfile } from '@/context/ProfileContext';
import './Header.css';

export default function Header() {
  const { user, isAuthenticated, logout } = useAuth();
  const { profile } = useProfile();
  const displayName = profile?.nickname || profile?.displayName?.split(' ')[0] || user?.name?.split(' ')[0];
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className={`site-header ${scrolled ? 'site-header--scrolled' : ''}`}>
      <div className="header-inner container">

        {/* Logo */}
        <Link to="/" className="header-logo" onClick={() => setMenuOpen(false)}>
          <LogoSvg />
          <div className="header-logo-text">
            <span className="header-logo-title">The Gardeners</span>
            <span className="header-logo-subtitle">Calendar</span>
          </div>
        </Link>

        {/* Main nav — desktop */}
        <nav className="header-nav" aria-label="Main navigation">
          <NavLink to="/plants" className={({ isActive }) => `nav-link ${isActive ? 'nav-link--active' : ''}`}>
            Plants
          </NavLink>
          <NavLink to="/biodynamic" className={({ isActive }) => `nav-link ${isActive ? 'nav-link--active' : ''}`}>
            Biodynamic
          </NavLink>
          <NavLink to="/moon-planting" className={({ isActive }) => `nav-link ${isActive ? 'nav-link--active' : ''}`}>
            Moon Planting
          </NavLink>
          <NavLink to="/advice" className={({ isActive }) => `nav-link ${isActive ? 'nav-link--active' : ''}`}>
            Advice
          </NavLink>
          <NavLink to="/directory" className={({ isActive }) => `nav-link ${isActive ? 'nav-link--active' : ''}`}>
            Directory
          </NavLink>
          {isAuthenticated && (
            <NavLink to="/blogs" className={({ isActive }) => `nav-link ${isActive ? 'nav-link--active' : ''}`}>
              Blogs
            </NavLink>
          )}
          {isAuthenticated && (
            <NavLink to="/my-garden" className={({ isActive }) => `nav-link ${isActive ? 'nav-link--active' : ''}`}>
              My Garden
            </NavLink>
          )}
        </nav>

        {/* Auth actions */}
        <div className="header-actions">
          {isAuthenticated ? (
            <div className="header-user">
              <NavLink to="/dashboard" className="header-dashboard-link">
                <span className="header-user-avatar">
                  {user?.name?.charAt(0).toUpperCase() ?? 'G'}
                </span>
                <span className="header-user-name">{displayName}</span>
              </NavLink>
              <button className="header-logout" onClick={() => { logout(); navigate('/'); }} title="Sign out">
                <LogoutIcon />
              </button>
            </div>
          ) : (
            <Link to="/login" className="btn-sign-in">
              Sign in
            </Link>
          )}

          {/* Hamburger */}
          <button
            className={`hamburger ${menuOpen ? 'hamburger--open' : ''}`}
            onClick={() => setMenuOpen(v => !v)}
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
          >
            <span /><span /><span />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`mobile-menu ${menuOpen ? 'mobile-menu--open' : ''}`} aria-hidden={!menuOpen}>
        <nav className="mobile-nav">
          {[
            { to: '/plants', label: 'Plants', emoji: '🌱' },
            { to: '/biodynamic', label: 'Biodynamic', emoji: '🌕' },
            { to: '/moon-planting', label: 'Moon Planting', emoji: '🌙' },
            { to: '/advice', label: 'Advice', emoji: '📖' },
            { to: '/directory', label: 'Directory', emoji: '🔍' },
            ...(isAuthenticated ? [
              { to: '/blogs', label: 'Blogs', emoji: '📖' },
              { to: '/my-garden', label: 'My Garden', emoji: '🏡' },
              { to: '/dashboard', label: 'Dashboard', emoji: '🗓' },
            ] : []),
          ].map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `mobile-nav-link ${isActive ? 'mobile-nav-link--active' : ''}`}
              onClick={() => setMenuOpen(false)}
            >
              <span className="mobile-nav-emoji">{item.emoji}</span>
              {item.label}
            </NavLink>
          ))}
          {!isAuthenticated && (
            <Link to="/login" className="mobile-nav-link mobile-nav-link--cta" onClick={() => setMenuOpen(false)}>
              <span className="mobile-nav-emoji">🌿</span>
              Sign in / Register
            </Link>
          )}
          {isAuthenticated && (
            <button className="mobile-nav-link mobile-nav-link--logout" onClick={() => { logout(); setMenuOpen(false); navigate('/'); }}>
              <span className="mobile-nav-emoji">👋</span>
              Sign out
            </button>
          )}
        </nav>
      </div>
    </header>
  );
}

function LogoSvg() {
  return (
    <svg viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" className="header-logo-icon" aria-hidden="true">
      {/* Circular wreath */}
      <circle cx="22" cy="22" r="20" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 2" opacity="0.3"/>
      {/* Left leaf */}
      <path d="M22 28 C22 28 14 22 12 16 C14 14 20 16 22 22" fill="currentColor" opacity="0.7"/>
      {/* Right leaf */}
      <path d="M22 28 C22 28 30 22 32 16 C30 14 24 16 22 22" fill="var(--sage-light)" opacity="0.9"/>
      {/* Stem */}
      <path d="M22 22 L22 34" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      {/* Roots */}
      <path d="M22 34 C20 36 18 36 17 38 M22 34 C24 36 26 36 27 38 M22 34 L22 38" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
      {/* Sun/moon circle at top */}
      <circle cx="22" cy="10" r="3" fill="var(--ochre)" opacity="0.8"/>
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16" aria-hidden="true">
      <path d="M7 3H4a1 1 0 00-1 1v12a1 1 0 001 1h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M13 14l3-4-3-4M16 10H8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
